// Odoo 350042: двухфакторный вход — passkey (WebAuthn), приложение-аутентификатор (TOTP), коды восстановления.
// Контракт бэка: scm_core_backend .claude/api/mfa.md (POST /auth/mfa/<action>, только core).
// Здесь чистые функции: base64url ⇄ ArrayBuffer, перевод options бэка в вид navigator.credentials.*,
// ответ браузера → JSON (WebAuthn L3 toJSON()), коды ошибок → текст для человека, вызов API.
import { t } from '@/i18n'

export const MFA_BASE = '/auth/mfa/'

// ── base64url ⇄ ArrayBuffer ─────────────────────────────────────────────────────────────
export function b64urlToBuf(s) {
    const str = String(s || '').replace(/-/g, '+').replace(/_/g, '/')
    const pad = str.length % 4 ? '='.repeat(4 - (str.length % 4)) : ''
    const bin = atob(str + pad)
    const out = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
    return out.buffer
}

export function bufToB64url(buf) {
    if (buf == null) return null
    const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength)
    let bin = ''
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const decodeDescriptors = (list) => (list || []).map(d => Object.assign({}, d, { id: b64urlToBuf(d.id) }))

// publicKey из webauthn/register/begin → опции navigator.credentials.create
export function creationOptions(pk) {
    const o = Object.assign({}, pk)
    o.challenge = b64urlToBuf(pk.challenge)
    o.user = Object.assign({}, pk.user, { id: b64urlToBuf(pk.user && pk.user.id) })
    if (pk.excludeCredentials) o.excludeCredentials = decodeDescriptors(pk.excludeCredentials)
    return o
}

// publicKey из webauthn/authenticate/begin → опции navigator.credentials.get
export function requestOptions(pk) {
    const o = Object.assign({}, pk)
    o.challenge = b64urlToBuf(pk.challenge)
    if (pk.allowCredentials) o.allowCredentials = decodeDescriptors(pk.allowCredentials)
    return o
}

// PublicKeyCredential → JSON для finish (все бинарные поля base64url). Собственный toJSON() не используем:
// в Safari и старых Chromium его нет или он неполон.
export function credentialToJSON(cred) {
    const r = cred.response || {}
    const response = { clientDataJSON: bufToB64url(r.clientDataJSON) }
    if (r.attestationObject) {
        response.attestationObject = bufToB64url(r.attestationObject)
        let transports = []
        try { transports = typeof r.getTransports === 'function' ? r.getTransports() || [] : [] } catch (e) { /* нет — пусто */ }
        response.transports = transports
    }
    if (r.authenticatorData) response.authenticatorData = bufToB64url(r.authenticatorData)
    if (r.signature) response.signature = bufToB64url(r.signature)
    if ('userHandle' in r) response.userHandle = r.userHandle ? bufToB64url(r.userHandle) : null
    let ext = {}
    try { ext = typeof cred.getClientExtensionResults === 'function' ? cred.getClientExtensionResults() || {} : {} } catch (e) { /* нет — пусто */ }
    return {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type || 'public-key',
        response,
        clientExtensionResults: ext,
        authenticatorAttachment: cred.authenticatorAttachment || undefined,
    }
}

// passkey в этом браузере возможен: API есть и контекст защищённый (https / localhost)
export function passkeySupported(w = (typeof window !== 'undefined' ? window : null)) {
    return !!(w && w.PublicKeyCredential && w.isSecureContext !== false && w.navigator && w.navigator.credentials)
}

// имя нового passkey по умолчанию — устройство из User-Agent (человек переименует в профиле позже, если нужно)
export function passkeyDefaultName(ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '')) {
    const s = String(ua || '')
    const os = /iPhone/.test(s) ? 'iPhone' : /iPad/.test(s) ? 'iPad' : /Android/.test(s) ? 'Android'
        : /Mac OS X|Macintosh/.test(s) ? 'Mac' : /Windows/.test(s) ? 'Windows' : /Linux/.test(s) ? 'Linux' : ''
    const br = /Edg\//.test(s) ? 'Edge' : /Firefox\//.test(s) ? 'Firefox' : /Chrome\//.test(s) ? 'Chrome' : /Safari\//.test(s) ? 'Safari' : ''
    return [os, br].filter(Boolean).join(' · ') || t('Passkey')
}

// секрет из otpauth://…?secret=… — для ручного ввода в приложение
export function otpauthSecret(uri) {
    const m = /[?&]secret=([^&]+)/i.exec(String(uri || ''))
    return m ? decodeURIComponent(m[1]) : ''
}

// «ABCD EFGH …» — секрет группами по 4 для чтения глазами
export function groupSecret(s) {
    return String(s || '').replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim()
}

// ввод кода: только цифры (TOTP) / буквы-цифры-дефис (код восстановления), без пробелов
export function normalizeCode(code, method) {
    const s = String(code || '').trim()
    return method === 'totp' ? s.replace(/\D/g, '') : s.replace(/\s+/g, '').toLowerCase()
}

// ── ошибки → текст ──────────────────────────────────────────────────────────────────────
// Бэк отдаёт notification.text только по-английски, поэтому переводим по коду ошибки.
export function mfaErrorText(code, extra = {}) {
    switch (code) {
        case 'invalid_code':
            return extra.attempts_left != null
                ? t('Wrong code. Attempts left: {n}', { n: extra.attempts_left })
                : t('Wrong code. Try again')
        case 'ticket_expired':
        case 'ticket_mismatch':
            return t('The sign-in has expired. Please sign in again.')
        case 'locked':
            return t('Too many failed attempts. Try again in 15 minutes.')
        case 'challenge_expired':
            return t('The passkey request has expired. Try again.')
        case 'invalid_passkey':
            return t('This passkey is not registered for your account.')
        case 'sso_only':
            return t('Your organization requires sign-in with Google or Microsoft.')
        case 'mfa_unavailable':
            return t('Two-factor sign-in is temporarily unavailable. Contact your administrator.')
        case 'reauth_required':
            return t('Confirm it is you first.')
        case 'reauth_failed':
            return t('Could not confirm it is you. Check the password or code.')
        case 'last_factor_required':
            return t('Your organization requires two-factor sign-in: this is your last method and cannot be removed.')
        case 'totp_exists':
            return t('An authenticator app is already set up.')
        case 'no_factor':
            return t('Set up a passkey or an authenticator app first.')
        case 'bad_origin':
            return t('This page address is not allowed for two-factor sign-in.')
        case 'not_authenticated':
            return t('Your session has ended. Please sign in again.')
        case 'bad_method':
            return t('No passkey is registered for this account.')
        case 'busy':
            return t('Please try again.')
        case 'network':
            return t('Could not reach the server. Check your connection and try again')
        // ошибки браузера (DOMException.name)
        case 'NotAllowedError':
            return t('The passkey request was cancelled or timed out.')
        case 'InvalidStateError':
            return t('This passkey is already registered.')
        case 'SecurityError':
            return t('Passkeys are not allowed on this page address.')
        case 'NotSupportedError':
            return t('This browser or device does not support passkeys.')
        default:
            return t('Two-factor sign-in failed. Try again.')
    }
}

// ошибка ответа (jqXHR или {status, data}) → { code, text, extra }
export function mfaFailure(xhrOrRes) {
    const status = Number(xhrOrRes && xhrOrRes.status) || 0
    let data = xhrOrRes && (xhrOrRes.data || xhrOrRes.responseJSON)
    if (!data && xhrOrRes && typeof xhrOrRes.responseText === 'string' && xhrOrRes.responseText.trim().startsWith('{')) {
        try { data = JSON.parse(xhrOrRes.responseText) } catch (e) { /* не JSON */ }
    }
    const code = (data && data.error) || (status === 0 ? 'network' : status === 429 ? 'locked' : '')
    return { code, status, extra: data || {}, text: mfaErrorText(code, data || {}) }
}

// ошибка navigator.credentials.* → { code, text }
export function webauthnFailure(e) {
    const code = (e && e.name) || ''
    return { code, status: 0, extra: {}, text: mfaErrorText(code) }
}

// тикет погас — назад к паролю
export const TICKET_GONE = ['ticket_expired', 'ticket_mismatch', 'locked']

// ── вызов API ───────────────────────────────────────────────────────────────────────────
// → { ok, status, data }; сеть упала — status 0. Тело ответа не логируем: в нём бывают коды восстановления и otpauth-секрет.
export async function mfaCall(action, body = {}, fetchImpl = (typeof fetch !== 'undefined' ? fetch : null)) {
    let res
    try {
        res = await fetchImpl(MFA_BASE + action, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body || {}),
        })
    } catch (e) {
        return { ok: false, status: 0, data: { error: 'network' } }
    }
    let data = {}
    try { data = await res.json() } catch (e) { data = {} }
    const ok = res.ok && (!data || data.status !== 'error')
    return { ok, status: res.status, data: data || {} }
}
